<script lang="ts">
import { tv } from 'tailwind-variants';
import { CalendarEventTimings, CalendarEventVariants } from '#layers/listing/constants/calendar';

// Past items keep opaque fills, so blocks side by side in the time grid never show through each other.
const calendarEventContent = tv({
	slots: {
		root: 'group flex size-full overflow-hidden text-xs whitespace-nowrap data-past:text-muted',
		body: 'contents',
		dot: 'ms-0.5 me-1.75 size-1.5 shrink-0 rounded-full bg-primary group-data-past:bg-(--cal-past-accent)',
		title: 'min-w-0 truncate',
		time: 'tabular-nums',
	},
	variants: {
		variant: {
			[CalendarEventVariants.Dot]: {
				root: 'items-center rounded-sm px-1 text-default hover:bg-elevated',
				time: 'sr-only',
			},
			[CalendarEventVariants.Bar]: {
				root: 'items-center rounded-sm bg-(--cal-item-strong) px-1.5 font-medium text-(--cal-item-text) data-past:bg-(--cal-past-item-strong)',
				time: 'sr-only',
			},
			// Puts a short block's title and time on one line, and hides the time visually when it is also narrow.
			[CalendarEventVariants.Block]: {
				root: 'block rounded-md bg-(--cal-block) ring-1 ring-(--cal-block-ring) ring-inset container-size hover:bg-(--cal-block-hover) data-live:bg-(--cal-live-item) data-live:ring-(--cal-live-ring) data-past:bg-muted data-past:ring-default',
				body: `
					flex h-full flex-col gap-0.5 px-2.5 py-1.5
					[@container(max-height:2.75rem)]:flex-row [@container(max-height:2.75rem)]:items-baseline
					[@container(max-height:2.75rem)]:gap-2 [@container(max-height:2.75rem)]:py-1
				`,
				title: 'font-medium text-highlighted group-data-past:text-muted',
				time: `
					shrink-0 truncate text-(--cal-block-time)
					group-data-live:font-medium group-data-live:text-primary
					group-data-past:text-dimmed
					[@container(max-height:2.75rem)_and_(max-width:10rem)]:sr-only
				`,
			},
			[CalendarEventVariants.AllDay]: {
				root: 'items-center rounded-md bg-(--cal-block) px-2 font-medium text-highlighted ring-1 ring-(--cal-block-ring) ring-inset hover:bg-(--cal-block-hover) data-past:bg-muted data-past:ring-default',
				time: 'sr-only',
			},
			// Wraps a long time range inside its column, so it never runs into the title.
			[CalendarEventVariants.Row]: {
				root: 'items-baseline',
				title: 'order-last text-sm font-medium text-highlighted',
				time: `
					w-37 shrink-0 pe-3 text-balance whitespace-normal text-muted
					group-data-live:font-medium group-data-live:text-primary
				`,
			},
		},
	},
});
</script>

<script setup lang="ts">
import type { Component } from 'vue';
import type { DisplayTemplateAst, GlobalCollectionId } from '#layers/data-model/public';
import type { CalendarEventVariant } from '#layers/listing/constants/calendar';
import type { CalendarItemEvent } from './calendar-events';
import { createReusableTemplate } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserLocale } from '#layers/base/public';
import { isEmptyDisplayTemplate } from '#layers/data-model/public';
import { formatCalendarEventTime } from './format-event-time';

const {
	calendarEvent,
	variant,
} = defineProps<{
	calendarEvent: CalendarItemEvent;
	variant: CalendarEventVariant;
	titleTemplate: DisplayTemplateAst | null;
	collectionId: GlobalCollectionId;
	renderDisplayTemplate: Component;
}>();

const { t } = useI18n();
const { formatLocale } = useUserLocale();

const ui = computed(() => calendarEventContent({ variant }));

// List shows every item at full strength; only the Month, Week and Day grids mute past items.
const isMuted = computed(() => calendarEvent.timing === CalendarEventTimings.Past && variant !== CalendarEventVariants.Row);

const isLive = computed(() => calendarEvent.timing === CalendarEventTimings.Live);

// Mounts a tooltip only for variants too small to print their time, since a view can hold hundreds of items.
const showsTime = computed(() => variant === CalendarEventVariants.Block || variant === CalendarEventVariants.Row);

const timeLabel = computed(() => formatCalendarEventTime(calendarEvent, formatLocale.value) ?? t('layout.calendar.all_day'));

const [DefineEvent, ReuseEvent] = createReusableTemplate();
</script>

<!--
	Schedule-X strips an event's default background, padding and border whenever a custom event
	slot is supplied, expecting the slot to own its appearance.
-->
<template>
	<DefineEvent>
		<div
			data-testid="calendar-event"
			:data-past="isMuted || undefined"
			:data-live="isLive || undefined"
			:class="ui.root()"
		>
			<div :class="ui.body()">
				<span
					v-if="variant === CalendarEventVariants.Dot"
					:class="ui.dot()"
				/>
				<span :class="ui.title()">
					<component
						:is="renderDisplayTemplate"
						v-if="!isEmptyDisplayTemplate(titleTemplate)"
						:item="calendarEvent.item"
						:display-template="titleTemplate"
						:collection-id
						:root="false"
						:truncation-tooltip="false"
					/>
					<template v-else>
						{{ calendarEvent.title }}
					</template>
				</span>
				<span :class="ui.time()">
					{{ timeLabel }}
				</span>
			</div>
		</div>
	</DefineEvent>

	<ReuseEvent v-if="showsTime" />
	<DTooltip
		v-else
		:text="timeLabel"
	>
		<ReuseEvent />
	</DTooltip>
</template>
